import { Button, Flex, Modal } from '@mantine/core';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type {
	DataKYCFileTct,
	KycFileCertificationChoiceDto,
} from '@dealstack/shared/lib/generated';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { CertificationChoiceFields } from './CertificationChoiceFields';
import { certificationChoiceDefaults } from './CertifiableProof.utils';
import { useCertificationChoiceForm } from './useCertificationChoiceForm';

interface Props {
	kycFile: DataKYCFileTct;
	isSaving: boolean;
	onSave: (choice: KycFileCertificationChoiceDto) => void;
	onClose: () => void;
}

export const ChangeCertificationModal = ({ kycFile, isSaving, onSave, onClose }: Props) => {
	const tInvestor = useTranslationInvestor();
	const { form, mode, choice } = useCertificationChoiceForm(certificationChoiceDefaults(kycFile));

	return (
		<Modal
			opened
			onClose={onClose}
			title={tInvestor('investment.kyc.certification.modal_title', {
				defaultValue: 'How is this document certified?',
			})}
			data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_MODAL}
		>
			<Flex direction="column" gap="1rem">
				<CertificationChoiceFields control={form.control} mode={mode} disabled={isSaving} />
				<Flex justify="flex-end" gap="0.5rem">
					<Button variant="outline" color="dark" onClick={onClose} disabled={isSaving}>
						{tInvestor('investment.kyc.certification.cancel', {
							defaultValue: 'Cancel',
						})}
					</Button>
					<Button
						onClick={() => choice && onSave(choice)}
						disabled={!choice}
						loading={isSaving}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_MODAL_SAVE}
					>
						{tInvestor('investment.kyc.certification.save', { defaultValue: 'Save' })}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
